import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CopyReport,
  type DatabaseTable,
  dbCan,
  useDatabase,
  useDuplicateTable,
  useMyDatabases,
} from '@/entities/database';
import { useWorkspaces } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, Icons, TextField } from '@/shared/ui';

import styles from './TableTabs.module.css';

const NAME_MAX = 100;

interface Props {
  table: DatabaseTable;
  onDuplicated: (table: DatabaseTable, report: CopyReport) => void;
  onClose: () => void;
}

/**
 * "Duplicate table": a name, the destination database (this one or another where the viewer
 * manages the structure), structure only or with records. The copy follows the access of its
 * database - tables have none of their own - and confidential content only goes to confidential
 * databases (the server enforces it; the list simply does not offer anything else).
 */
export function DuplicateTableDialog({ table, onDuplicated, onClose }: Props) {
  const { t } = useTranslation();
  const duplicate = useDuplicateTable(table.id);
  const source = useDatabase(table.database_id);
  const mine = useMyDatabases();
  const workspaces = useWorkspaces();
  const [name, setName] = useState(() =>
    t('databases.duplicate.table.defaultName', { name: table.name }).slice(0, NAME_MAX),
  );
  const [target, setTarget] = useState(table.database_id);
  const [records, setRecords] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [touched, setTouched] = useState(false);

  const workspaceNames = new Map((workspaces.data ?? []).map((w) => [w.id, w.name]));
  const sourceDatabase = source.data;
  const confidentialSource = sourceDatabase?.is_confidential ?? false;
  const destinations = (mine.data ?? []).filter(
    (db) =>
      dbCan(db, 'database.manage_structure') &&
      (db.id === table.database_id || !confidentialSource || db.is_confidential),
  );
  const destination = destinations.find((db) => db.id === target);
  const cross =
    destination !== undefined &&
    sourceDatabase !== undefined &&
    destination.workspace_id !== sourceDatabase.workspace_id;
  const needsConfirmation = confidentialSource && cross;
  const nameError = touched && name.trim() === '' ? t('validation.REQUIRED') : undefined;

  const submit = () => {
    setTouched(true);
    if (name.trim() === '' || (needsConfirmation && !confirmed)) return;
    duplicate.mutate(
      {
        name: name.trim(),
        ...(target !== table.database_id && { database_id: target }),
        include_records: records,
        ...(needsConfirmation && { confirm_cross_workspace: true }),
      },
      {
        onSuccess: ({ table: copy, report }) => {
          onDuplicated(copy, report);
        },
      },
    );
  };

  return (
    <Dialog title={t('databases.duplicate.table.title', { name: table.name })} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t('databases.duplicate.table.name')}
          value={name}
          maxLength={NAME_MAX}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        {destinations.length > 1 && (
          <Combobox
            label={t('databases.duplicate.table.database')}
            value={target}
            options={destinations.map((db) => ({
              value: db.id,
              label:
                db.id === table.database_id
                  ? t('databases.duplicate.table.thisDatabase', { name: db.name })
                  : db.workspace_id === sourceDatabase?.workspace_id
                    ? db.name
                    : `${db.name} · ${workspaceNames.get(db.workspace_id) ?? ''}`,
            }))}
            onChange={(next) => {
              setTarget(next);
              setConfirmed(false);
            }}
          />
        )}
        <fieldset className={styles.modes}>
          <legend>{t('databases.duplicate.mode')}</legend>
          <label className={styles.choice}>
            <input
              type="radio"
              name="duplicate-table-mode"
              checked={!records}
              onChange={() => {
                setRecords(false);
              }}
            />
            <span>{t('databases.duplicate.structureOnly')}</span>
          </label>
          <label className={styles.choice}>
            <input
              type="radio"
              name="duplicate-table-mode"
              checked={records}
              onChange={() => {
                setRecords(true);
              }}
            />
            <span>{t('databases.duplicate.withRecords')}</span>
          </label>
        </fieldset>
        <p className={styles.notice} role="note">
          <Icons.lock /> {t('databases.duplicate.table.inheritNote')}
          {confidentialSource && ` ${t('databases.duplicate.table.downgrade')}`}
        </p>
        {records && (
          <p className={styles.warning} role="note">
            {t('databases.duplicate.personalData')}
            {cross && ` ${t('databases.duplicate.peopleDropped')}`}
          </p>
        )}
        {needsConfirmation && (
          <label className={styles.choice}>
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => {
                setConfirmed(event.target.checked);
              }}
            />
            <span>{t('databases.duplicate.table.confirmCross')}</span>
          </label>
        )}
        <FormError error={duplicate.error} />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={duplicate.isPending || (needsConfirmation && !confirmed)}
          >
            {duplicate.isPending
              ? t('databases.duplicate.copying')
              : t('databases.duplicate.submit')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
